/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import JsonLogic from 'json-logic-js';
import { parseISO, compareAsc } from 'date-fns';
import { get } from 'lodash';
import { DimensionType } from '@/components/cards/dashboards/types';
import { MetricOptions, DimensionOptions } from '@/constants/DashboardConstants';

export interface Item {
  color?: string;
  label: string;
  value: number;
}

interface FormattingRule {
  color: string;
  rule: any;
}

export const applyFormattingRules = (formattingRules: FormattingRule[], series: Item[]): Item[] => {
  return series.map(item => {
    for (const i in formattingRules) {
      const formattingRule = formattingRules[i];
      const match = JsonLogic.apply(formattingRule.rule, item);

      if (match) {
        return {
          ...item,
          color: formattingRule.color,
        };
      }
    }

    return item;
  });
};

export const userRoleCanEdit = (role: string): boolean => {
  return !['Reader', 'Contributor'].includes(role);
};

export const userCanSeeDrilldown = (role: string): boolean => {
  return !['Contributor', 'Reader'].includes(role);
};

export const sortSeriesByDateLabel = (a: Item, b: Item): number => {
  return compareAsc(parseISO(a.label), parseISO(b.label));
};

export const createValueFromLabel = (label: string): string => {
  let localLabel = label;
  switch (label) {
    case 'OrgGroup':
      localLabel = 'ORGANIZATION_GROUP';
      break;

    case 'SurveyResponse':
      localLabel = 'SURVEY_RESPONSE';
      break;

    case 'DataAsset':
      localLabel = 'DATA_ASSET';
      break;

    default:
      break;
  }
  return localLabel.toUpperCase().replace(' ', '_');
};

export const sortByLabel = (a, b) => a.label.localeCompare(b.label);

export const mapFilters = (filters: any[]) => {
  return filters.map(f => mapFilter(f));
};

const mapFilter = f => {
  const commonProps = {
    values: f.values.map(v => v.value),
    operator: 'AND',
  };
  if (f.name.type === DimensionType.ATTRIBUTE) {
    return {
      ...commonProps,
      type: f.name.type,
      attribute: f.name.value,
    };
  }
  if (f.name.type === DimensionType.MAPPING_ATTR) {
    return {
      ...commonProps,
      type: f.name.type,
      attribute: f.name.value,
      object: f.object.value,
    };
  }

  if (f.name.type === DimensionType.CUSTOM_ATTRIBUTE && !f.object) {
    return {
      ...commonProps,
      type: DimensionType.ATTRIBUTE,
      attribute: DimensionType.CUSTOM_ATTRIBUTE,
      customAttrType: f.name.customAttrType,
      customAttrId: f.name.value,
    };
  }

  if (f.name.type === DimensionType.CUSTOM_ATTRIBUTE && f.object) {
    return {
      ...commonProps,
      type: DimensionType.MAPPING_ATTR,
      attribute: DimensionType.CUSTOM_ATTRIBUTE,
      customAttrId: f.name.value,
      customAttrType: f.name.customAttrType,
      object: f.object.value,
    };
  }
};

export const mapBlockSettings = (
  selectedVisualization,
  selectedMetric,
  selectedDimension,
  selectedMappedObjectAttr,
  filters
) => {
  let settings = {
    metric: selectedMetric ? selectedMetric.value : null,
    visualization: selectedVisualization ? selectedVisualization.value : null,
  };

  if (!selectedDimension)
    return {
      ...settings,
      ...{ filters },
    };

  if (selectedDimension.type === DimensionType.ATTRIBUTE) {
    settings = {
      ...settings,
      ...{ dimension: selectedDimension.value },
    };
  }

  if (selectedDimension.type === DimensionType.MAPPING) {
    settings = {
      ...settings,
      ...{ mappedObject: selectedDimension.value },
    };
  }

  if (selectedDimension.type === DimensionType.MAPPING) {
    settings = {
      ...settings,
      ...{
        mappedObjectAttr: selectedMappedObjectAttr ? selectedMappedObjectAttr.value : null,
      },
    };
  }

  if (selectedDimension.type === DimensionType.CUSTOM_ATTRIBUTE) {
    settings = {
      ...settings,
      ...{
        dimension: DimensionType.CUSTOM_ATTRIBUTE,
        customAttrId: selectedDimension.value,
        customAttrLabel: selectedDimension.label,
        customAttrType: selectedDimension.attrType,
      },
    };
  }

  if (
    selectedDimension.type === DimensionType.MAPPING &&
    selectedMappedObjectAttr &&
    selectedMappedObjectAttr.type !== DimensionType.CUSTOM_ATTRIBUTE
  ) {
    settings = {
      ...settings,
      ...{ mappedObjectAttr: selectedMappedObjectAttr.value },
    };
  }

  if (
    selectedDimension.type === DimensionType.MAPPING &&
    selectedMappedObjectAttr &&
    selectedMappedObjectAttr.type === DimensionType.CUSTOM_ATTRIBUTE
  ) {
    settings = {
      ...settings,
      ...{
        mappedObjectAttr: DimensionType.CUSTOM_ATTRIBUTE,
        customAttrId: selectedMappedObjectAttr.value,
        customAttrType: selectedMappedObjectAttr.attrType,
      },
    };
  }

  return {
    ...settings,
    ...{ filters },
  };
};

export const getDimensionAxisLabel = blockSettings => {
  const { dimension, mappedObject, mappedObjectAttr, customAttrLabel } = blockSettings;

  const dimensionLabel = get(DimensionOptions, `${dimension}.label`);
  const mappedObjectLabel = get(MetricOptions, `${mappedObject}.label`);
  const mappedObjectAttributeLabel = get(DimensionOptions, `${mappedObjectAttr}.label`);

  if (mappedObject) {
    return `${mappedObjectLabel} ${mappedObjectAttributeLabel}`;
  } else if (dimension === 'CUSTOM_ATTRIBUTE') {
    return customAttrLabel;
  } else {
    return dimensionLabel;
  }
};
